import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { practicePaymentApi } from "../apis";
import type {
  CurrentSubscriptionResponseDto,
  TransactionDto,
  TransactionPaginationDto,
} from "../libs/cellera-sdk";

export enum PlanDisplayName {
  MONTHLY = "Monthly Plan",
  SIX_MONTHS = "Six Months Plan",
  UNKNOWN = "Unknown Plan",
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  INCOMPLETE = "incomplete",
  INCOMPLETE_EXPIRED = "incomplete_expired",
  PAUSED = "paused",
  TRIALING = "trialing",
  UNPAID = "unpaid",
}

export enum TransactionStatus {
  PAID = "paid",
  FAILED = "failed",
  PENDING = "pending",
}

export enum Currency {
  USD = "$",
}

export const API_CONFIG = {
  SUBSCRIPTION_STALE_TIME: 5 * 60 * 1000,
  TRANSACTIONS_STALE_TIME: 2 * 60 * 1000,
  DEFAULT_PAGE_SIZE: 10,
  INITIAL_PAGE: 1,
} as const;

export interface SubscriptionData {
  planName: string;
  price: string;
  renewalDate: string;
  status: SubscriptionStatus;
}

export interface Transaction {
  id: string;
  datetime: string;
  item: string;
  amount: number;
  status: TransactionStatus;
  hostedInvoiceUrl?: string;
  pdfUrl?: string;
  currency?: string;
}

interface UsePracticeSubscriptionReturn {
  subscription: SubscriptionData | null;
  transactions: Transaction[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refetch: () => void;
  viewInvoice: (hostedInvoiceUrl: string) => void;
}

const convertSubscriptionData = (
  apiSubscription: CurrentSubscriptionResponseDto,
): SubscriptionData => {
  const PLAN_NAMES = {
    1: "Monthly Plan",
    6: "Six Months Plan",
  };
  const INTERVAL_NAMES = {
    month: "month",
    months: "months",
  };
  const planName =
    PLAN_NAMES[
      apiSubscription.pricing?.intervalCount as keyof typeof PLAN_NAMES
    ] ?? "Unknown Plan";

  const { pricing } = apiSubscription || {};
  const { amount = 0, intervalCount = 1 } = pricing || {};
  const intervalName =
    intervalCount === 1
      ? INTERVAL_NAMES.month
      : intervalCount === 6
        ? INTERVAL_NAMES.months
        : INTERVAL_NAMES.month;

  const price = pricing
    ? `${Currency.USD} ${(amount / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
      })}/${intervalCount === 1 ? "" : intervalCount} ${intervalName}`
    : "N/A";

  return {
    planName,
    price: price.replace(" ", ""),
    renewalDate: apiSubscription.renewalDateTime?.toISOString() || "",
    status: apiSubscription.status as SubscriptionStatus,
  };
};

const fetchAdminPracticeSubscription = async (
  practiceId: string,
): Promise<CurrentSubscriptionResponseDto> => {
  try {
    return await practicePaymentApi.adminGetPracticeSubscription({
      practiceId,
    });
  } catch (error) {
    console.error("Error fetching admin practice subscription:", error);
    throw error;
  }
};

const fetchAdminPracticeTransactions = async (
  practiceId: string,
  page: number = API_CONFIG.INITIAL_PAGE,
  pageSize: number = API_CONFIG.DEFAULT_PAGE_SIZE,
): Promise<TransactionPaginationDto> => {
  try {
    return await practicePaymentApi.adminGetPracticeTransactions({
      practiceId,
      page,
      pageSize,
    });
  } catch (error) {
    console.error("Error fetching admin practice transactions:", error);
    throw error;
  }
};

export const usePracticeSubscription = (
  practiceId: string,
): UsePracticeSubscriptionReturn => {
  const [page, setPage] = useState<number>(API_CONFIG.INITIAL_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    API_CONFIG.DEFAULT_PAGE_SIZE,
  );

  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ["admin-practice-subscription", practiceId],
    queryFn: () => fetchAdminPracticeSubscription(practiceId),
    staleTime: API_CONFIG.SUBSCRIPTION_STALE_TIME,
    refetchOnWindowFocus: false,
    enabled: Boolean(practiceId),
  });

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ["admin-practice-transactions", practiceId, page, pageSize],
    queryFn: () => fetchAdminPracticeTransactions(practiceId, page, pageSize),
    staleTime: API_CONFIG.TRANSACTIONS_STALE_TIME,
    refetchOnWindowFocus: false,
    enabled: Boolean(practiceId),
  });

  const viewInvoice = useCallback((hostedInvoiceUrl: string) => {
    window.open(hostedInvoiceUrl, "_blank", "noopener,noreferrer");
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage + 1);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(API_CONFIG.INITIAL_PAGE);
  }, []);

  const refetch = useCallback(() => {
    refetchSubscription();
    refetchTransactions();
  }, [refetchSubscription, refetchTransactions]);

  const loading = subscriptionLoading || transactionsLoading;
  const error =
    subscriptionError?.message || transactionsError?.message || null;

  return {
    subscription: subscriptionData
      ? convertSubscriptionData(subscriptionData)
      : null,
    transactions:
      transactionsData?.data?.map((transaction: TransactionDto) => ({
        id: transaction.id,
        datetime: transaction.datetime.toISOString(),
        item: transaction.item,
        amount: transaction.totalAmount,
        status: transaction.status as TransactionStatus,
        currency: transaction.currency,
        hostedInvoiceUrl: transaction.hostedInvoiceUrl,
        pdfUrl: transaction.pdfUrl,
      })) || [],
    totalCount: transactionsData?.total || 0,
    loading,
    error,
    page: page - 1,
    pageSize,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    refetch,
    viewInvoice,
  };
};
